import React, { useEffect, useState } from 'react';
import { Box, Button, FormControl, InputLabel, MenuItem, Select, TextField, Typography, SelectChangeEvent } from '@mui/material';
import { ItemService } from '../../services/item.service';
import { StockService } from '../../services/stock.service';
import { getStoresByInstitutionId } from '../../services/store.service';
import { Item } from '../../interfaces/item.interface';
import { Store } from '../../interfaces/store.interface';
import { useToast } from '../../context/ToastContext';
import { TEXT_AREA_MAX_LENGTH } from '../../constants/fieldLimits';

const StockIn: React.FC = () => {
    const [items, setItems] = useState<Item[]>([]);
    const [stores, setStores] = useState<Store[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedItemId, setSelectedItemId] = useState<string>('');
    const [selectedItem, setSelectedItem] = useState<Item | null>(null);
    const [selectedStoreId, setSelectedStoreId] = useState<string>('');
    const [selectedStore, setSelectedStore] = useState<Store | null>(null);
    const [amount, setAmount] = useState<number>(0);
    const [unitCost, setUnitCost] = useState<number>(0);
    const [remarks, setRemarks] = useState<string>('');
    const toast = useToast();
    const [currentStock, setCurrentStock] = useState<number | null>(null);
    const [loadingStock, setLoadingStock] = useState<boolean>(false);

    useEffect(() => {
        fetchItems();
        fetchStores();
    }, []);

    const fetchItems = async () => {
        try {
            setLoading(true);
            const response = await ItemService.getItemsByInstitution(0, 100);
            setItems(response.content);
        } catch (err) {
            toast.showApiError(err, 'Failed to load items');
            console.error('Error fetching items:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchStores = async () => {
        try {
            setLoading(true);
            // Assuming you have a service to fetch stores
            const response = await getStoresByInstitutionId(); // Replace with actual service call
            setStores(response.content);
        } catch (err) {
            toast.showApiError(err, 'Failed to load stores');
            console.error('Error fetching stores:', err);
        } finally {
            setLoading(false);
        }
    }

    const handleItemChange = (event: SelectChangeEvent) => {
        const id = event.target.value;
        setSelectedItemId(id);

        const item = items.find(item => item.id === id);
        setSelectedItem(item || null);

        // Reset other fields
        setAmount(0);
        setUnitCost(0);
    };

    const handleStoreChange = (event: SelectChangeEvent) => {
        const id = event.target.value;
        setSelectedStoreId(id);

        const store = stores.find(store => store.id === id);
        setSelectedStore(store || null);

        // Reset other fields
        setAmount(0);
    };

    // Function to fetch current stock amount when both store and item are selected
    const fetchCurrentStock = async () => {
        if (selectedStoreId && selectedItemId) {
            try {
                setLoadingStock(true);
                setCurrentStock(null);
                const stockData = await StockService.getStockByStoreAndItem(selectedStoreId, selectedItemId);
                setCurrentStock(stockData.availableQty);
            } catch (err) {
                console.error('Error fetching current stock:', err);
                setCurrentStock(0); // Default to 0 if there's an error or no stock exists
            } finally {
                setLoadingStock(false);
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedItemId) {
            toast.showError('Please select an item');
            return;
        }

        if (amount <= 0) {
            toast.showError('Please enter a valid amount (greater than 0)');
            return;
        }

        if (selectedItem?.lotTracked && unitCost <= 0) {
            toast.showError('Please enter a valid unit cost (greater than 0) for this lot-tracked item');
            return;
        }

        try {
            const updatedStock = await StockService.stockIn({
                storeId: selectedStoreId,
                itemId: selectedItemId,
                amount: amount,
                ...(selectedItem?.lotTracked ? { unitCost } : {}),
                remarks: remarks || undefined,
            });

            // Update selected item with new quantity and refresh the display
            if (updatedStock) {
                setSelectedItem(updatedStock.item);
                setCurrentStock(updatedStock.availableQty);
                toast.showSuccess(`Stock in successful. New quantity: ${updatedStock?.availableQty}`);
            } else {
                toast.showSuccess('Stock in successful');
            }

            // Reset the form
            setAmount(0);
            setUnitCost(0);
            setRemarks('');
        } catch (error) {
            console.error('Error during stock in:', error);
            toast.showApiError(error, 'Failed to process stock in');
        }
    };

    useEffect(() => {
        fetchCurrentStock();
    }, [selectedStoreId, selectedItemId]);

    if (loading) {
        return <div data-testid="loading-state">Loading...</div>;
    }

    return (
        <div style={{ padding: '20px' }}>
            <Typography variant="h4" gutterBottom>
                Stock In
            </Typography>

            <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 500 }}>
                <FormControl fullWidth margin="normal" required>
                    <InputLabel>Select Item</InputLabel>
                    <Select
                        value={selectedItemId}
                        label="Select Item"
                        onChange={handleItemChange}
                        data-testid="item-select"
                    >
                        {items.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                                {item.itemName} - Category: {item.itemCategory?.itemCategoryName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {selectedItem && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Item Details:
                        </Typography>
                        <Typography>
                            <strong>Name:</strong> {selectedItem.itemName}
                        </Typography>
                        <Typography>
                            <strong>Category:</strong> {selectedItem.itemCategory?.itemCategoryName}
                        </Typography>
                        <Typography>
                            <strong>Description:</strong> {selectedItem.description}
                        </Typography>
                    </Box>
                )}

                <FormControl fullWidth margin="normal" required>
                    <InputLabel>Select Store</InputLabel>
                    <Select
                        value={selectedStoreId}
                        label="Select Store"
                        onChange={handleStoreChange}
                        data-testid="store-select"
                    >
                        {stores.map((store) => (
                            <MenuItem key={store.id} value={store.id}>
                                {store.storeName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {selectedStore && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Store Details:
                        </Typography>
                        <Typography>
                            <strong>Name:</strong> {selectedStore.storeName}
                        </Typography>
                        <Typography>
                            <strong>Description:</strong> {selectedStore.description}
                        </Typography>
                        <Typography>
                            <strong>Address:</strong> {selectedStore.address}
                        </Typography>
                    </Box>
                )}

                {selectedStore && selectedItem && (
                    <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #bbdefb' }}>
                        {loadingStock ? (
                            <Typography>Loading current stock information...</Typography>
                        ) : (
                            <Typography variant="subtitle1" fontWeight="medium">
                                Current Stock: {currentStock !== null ? currentStock : 'Not available'}
                                {selectedItem?.unit ? ` ${selectedItem.unit.symbol}` : ''}
                            </Typography>
                        )}
                    </Box>
                )}

                <TextField
                    fullWidth
                    label={`Stock In Amount${selectedItem?.unit ? ` (${selectedItem.unit.symbol})` : ''}`}
                    type="number"
                    value={amount}
                    onChange={(e) => {
                        const value = Number(e.target.value);
                        if (value >= 0) {
                            setAmount(value);
                        }
                    }}
                    required
                    margin="normal"
                    InputProps={{
                        inputProps: { min: 0 }
                    }}
                    disabled={!selectedItem}
                    data-testid="amount-input"
                />

                {selectedItem?.lotTracked && (
                    <TextField
                        fullWidth
                        label="Unit Cost"
                        type="number"
                        value={unitCost}
                        onChange={(e) => {
                            const value = Number(e.target.value);
                            if (value >= 0) {
                                setUnitCost(value);
                            }
                        }}
                        required
                        margin="normal"
                        InputProps={{
                            inputProps: { min: 0, step: 'any' }
                        }}
                        error={unitCost <= 0}
                        helperText={unitCost <= 0 ? 'Unit cost is required and must be greater than 0' : ''}
                        data-testid="unit-cost-input"
                    />
                )}

                <TextField
                    fullWidth
                    label="Remarks (optional)"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    margin="normal"
                    multiline
                    rows={2}
                    disabled={!selectedItem}
                    data-testid="remarks-input"
                    inputProps={{ maxLength: TEXT_AREA_MAX_LENGTH }}
                    helperText={`${remarks.length}/${TEXT_AREA_MAX_LENGTH}`}
                />

                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    disabled={!selectedItem}
                    data-testid="submit-button"
                >
                    Stock In
                </Button>
            </Box>

        </div>
    );
};

export default StockIn;
